/**
 * Consultant Settings Page
 *
 * Allows consultant users to manage their account settings, preferences,
 * professional profile, and service rates.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Save, User, Bell, Lock, Briefcase, Globe, DollarSign } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Button } from '../../components/common/Button';

export const ConsultantSettingsPage: React.FC = () => {
  const { t } = useTranslation(['common', 'consultant']);
  const [activeTab, setActiveTab] = useState<'profile' | 'professional' | 'notifications' | 'security' | 'preferences' | 'billing'>('profile');

  // Mock data - would come from API
  const [formData, setFormData] = useState({
    // Profile
    name: 'Dr. Mohamed Halal Consultant',
    email: 'mohamed@halalconsulting.com',
    phone: '+60123456789',

    // Professional
    certificationNumber: 'HC-2020-001',
    specialization: 'Food Manufacturing',
    yearsOfExperience: '15',
    qualifications: 'PhD in Food Science, Halal Certification',
    serviceAreas: 'Selangor, Kuala Lumpur, Negeri Sembilan',

    // Billing
    hourlyRate: '250',
    currency: 'MYR',
    paymentTerms: 'Net 30',
    taxId: 'TIN123456789',

    // Notifications
    emailNotifications: true,
    clientUpdates: true,
    auditReminders: true,
    paymentNotifications: true,

    // Preferences
    language: 'en-US',
    timezone: 'Asia/Kuala_Lumpur',
    dateFormat: 'DD/MM/YYYY',
  });

  const handleSave = () => {
    // API call to save settings
    alert(t('common:messages.saveSuccess'));
  };

  const tabs = [
    { id: 'profile', label: 'User Profile', icon: <User className="w-4 h-4" /> },
    { id: 'professional', label: 'Professional Info', icon: <Briefcase className="w-4 h-4" /> },
    { id: 'billing', label: 'Billing & Rates', icon: <DollarSign className="w-4 h-4" /> },
    { id: 'notifications', label: 'Notifications', icon: <Bell className="w-4 h-4" /> },
    { id: 'security', label: 'Security', icon: <Lock className="w-4 h-4" /> },
    { id: 'preferences', label: 'Preferences', icon: <Globe className="w-4 h-4" /> },
  ];

  return (
    <PageLayout
      pageTitle={t('common:navigation.settings')}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('common:navigation.settings') },
      ]}
    >
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar Tabs */}
        <div className="lg:w-64 shrink-0">
          <Card>
            <CardBody className="p-2">
              <nav className="space-y-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                      activeTab === tab.id
                        ? 'bg-primary-50 text-primary-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {tab.icon}
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </CardBody>
          </Card>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold">User Profile</h3>
                <p className="text-sm text-gray-600">Manage your personal information</p>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button variant="primary" icon={<Save className="w-4 h-4" />} onClick={handleSave}>
                      {t('common:actions.save')}
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Professional Tab */}
          {activeTab === 'professional' && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold">Professional Information</h3>
                <p className="text-sm text-gray-600">Update your professional credentials</p>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Certification Number
                      </label>
                      <input
                        type="text"
                        value={formData.certificationNumber}
                        onChange={(e) => setFormData({ ...formData, certificationNumber: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Years of Experience
                      </label>
                      <input
                        type="number"
                        value={formData.yearsOfExperience}
                        onChange={(e) => setFormData({ ...formData, yearsOfExperience: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Specialization
                      </label>
                      <input
                        type="text"
                        value={formData.specialization}
                        onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Qualifications
                      </label>
                      <textarea
                        value={formData.qualifications}
                        onChange={(e) => setFormData({ ...formData, qualifications: e.target.value })}
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Service Areas
                      </label>
                      <input
                        type="text"
                        value={formData.serviceAreas}
                        onChange={(e) => setFormData({ ...formData, serviceAreas: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="e.g., Selangor, Kuala Lumpur"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button variant="primary" icon={<Save className="w-4 h-4" />} onClick={handleSave}>
                      {t('common:actions.save')}
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Billing Tab */}
          {activeTab === 'billing' && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold">Billing & Service Rates</h3>
                <p className="text-sm text-gray-600">Manage your billing information</p>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Hourly Rate
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          value={formData.hourlyRate}
                          onChange={(e) => setFormData({ ...formData, hourlyRate: e.target.value })}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                        <select
                          value={formData.currency}
                          onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                          className="w-24 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="MYR">MYR</option>
                          <option value="USD">USD</option>
                          <option value="SGD">SGD</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Terms
                      </label>
                      <select
                        value={formData.paymentTerms}
                        onChange={(e) => setFormData({ ...formData, paymentTerms: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="Net 15">Net 15</option>
                        <option value="Net 30">Net 30</option>
                        <option value="Net 60">Net 60</option>
                        <option value="Due on Receipt">Due on Receipt</option>
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tax ID / Business Registration Number
                      </label>
                      <input
                        type="text"
                        value={formData.taxId}
                        onChange={(e) => setFormData({ ...formData, taxId: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button variant="primary" icon={<Save className="w-4 h-4" />} onClick={handleSave}>
                      {t('common:actions.save')}
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold">Notification Preferences</h3>
                <p className="text-sm text-gray-600">Manage how you receive notifications</p>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3 border-b border-gray-200">
                    <div>
                      <p className="font-medium text-gray-900">Email Notifications</p>
                      <p className="text-sm text-gray-600">Receive notifications via email</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={formData.emailNotifications}
                      onChange={(e) => setFormData({ ...formData, emailNotifications: e.target.checked })}
                      className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div className="flex items-center justify-between py-3 border-b border-gray-200">
                    <div>
                      <p className="font-medium text-gray-900">Client Updates</p>
                      <p className="text-sm text-gray-600">Get notified about client activity</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={formData.clientUpdates}
                      onChange={(e) => setFormData({ ...formData, clientUpdates: e.target.checked })}
                      className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div className="flex items-center justify-between py-3 border-b border-gray-200">
                    <div>
                      <p className="font-medium text-gray-900">Audit Reminders</p>
                      <p className="text-sm text-gray-600">Receive reminders about upcoming audits</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={formData.auditReminders}
                      onChange={(e) => setFormData({ ...formData, auditReminders: e.target.checked })}
                      className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div className="flex items-center justify-between py-3">
                    <div>
                      <p className="font-medium text-gray-900">Payment Notifications</p>
                      <p className="text-sm text-gray-600">Get alerts about invoice payments</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={formData.paymentNotifications}
                      onChange={(e) => setFormData({ ...formData, paymentNotifications: e.target.checked })}
                      className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button variant="primary" icon={<Save className="w-4 h-4" />} onClick={handleSave}>
                      {t('common:actions.save')}
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold">Security Settings</h3>
                <p className="text-sm text-gray-600">Manage your password and security options</p>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Current Password
                    </label>
                    <input
                      type="password"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Password
                    </label>
                    <input
                      type="password"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button variant="primary" icon={<Save className="w-4 h-4" />} onClick={handleSave}>
                      Update Password
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Preferences Tab */}
          {activeTab === 'preferences' && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold">User Preferences</h3>
                <p className="text-sm text-gray-600">Customize your experience</p>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Language
                    </label>
                    <select
                      value={formData.language}
                      onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="en-US">English</option>
                      <option value="ms-MY">Bahasa Melayu</option>
                      <option value="zh-CN">简体中文</option>
                      <option value="ar-SA">العربية</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Timezone
                    </label>
                    <select
                      value={formData.timezone}
                      onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="Asia/Kuala_Lumpur">Kuala Lumpur (GMT+8)</option>
                      <option value="Asia/Singapore">Singapore (GMT+8)</option>
                      <option value="Asia/Jakarta">Jakarta (GMT+7)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date Format
                    </label>
                    <select
                      value={formData.dateFormat}
                      onChange={(e) => setFormData({ ...formData, dateFormat: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                      <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                      <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                    </select>
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button variant="primary" icon={<Save className="w-4 h-4" />} onClick={handleSave}>
                      {t('common:actions.save')}
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}
        </div>
      </div>
    </PageLayout>
  );
};
